import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdContentCopy, MdDeleteOutline, MdEdit, MdOutlineCheckCircle, MdOutlineRemoveCircleOutline, MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Utils, fetchWithAuth, selectStyles, copyToClipboard, getStatusDisplay, handleSessionExpired, DEFAULT_STATUS_OPTIONS, getDefaultPageSize, can, getAvatarInitials, nsAvatarColor, exportListCsv } from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const AppSupportCustomers = () => {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ customer_id: "", name: "", email: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ customer_id: "", name: "", email: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const [form, setForm] = useState({ id: null, customer_id: "", name: "", email: "", status: 1 });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const getCustomerActionOptionCount = () =>
    1 +
    (can("support.customers.edit") ? 1 : 0) +
    (can("support.customers.change_status") ? 1 : 0) +
    (can("support.customers.delete") ? 1 : 0);
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef,
    optionCount: getCustomerActionOptionCount,
    menuWidth: 220,
    useAnchored: true,
  });

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/customers`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        page: nextPage,
        pageSize,
        customer_id: (nextFilters.customer_id || "").trim(),
        name: (nextFilters.name || "").trim(),
        email: (nextFilters.email || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load customers");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  useEffect(() => {
    if (!appId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const customer_id = searchParams.get("customer_id") || "";
    const name = searchParams.get("name") || "";
    const email = searchParams.get("email") || "";
    const status = searchParams.get("status") || "";
    const f = { customer_id, name, email, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(customer_id || name || email || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.customer_id || "").trim()) sp.set("customer_id", f.customer_id.trim());
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if ((f.email || "").trim()) sp.set("email", f.email.trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.customer_id || "").trim() || (f.name || "").trim() || (f.email || "").trim() || (f.status || "").trim()));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { customer_id: "", name: "", email: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };


  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/customer/save`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, ...form }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to save customer");
    toast.success("Customer saved");
    setShowModal(false);
    setForm({ id: null, customer_id: "", name: "", email: "", status: 1 });
    loadData();
  };

  const toggleCustomerStatus = async (item) => {
    if (!item?.id) return;
    const nextStatus = Number(item.status) === 1 ? 0 : 1;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/customer/save`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        id: item.id,
        customer_id: item.customer_id,
        name: item.name,
        email: item.email || "",
        status: nextStatus,
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to update customer status");
    toast.success(nextStatus === 1 ? "Customer activated" : "Customer deactivated");
    loadData();
  };

  const remove = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This customer will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/customer/delete`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, id }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success("Customer deleted successfully");
    loadData();
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/support/customers", {
        app_id: appId,
        customer_id: (filters.customer_id || "").trim(),
        name: (filters.name || "").trim(),
        email: (filters.email || "").trim(),
        status: filters.status,
      }, "support-customers.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Support Customers</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("support.customers.add") ? (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                onClick={() => {
                  setForm({ id: null, customer_id: "", name: "", email: "", status: 1 });
                  setShowModal(true);
                }}
              >
                <MdAddCircle />
                <span>Add Customer</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Customer ID" value={draftFilters.customer_id} onChange={(e) => setDraftFilters((p) => ({ ...p, customer_id: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Name" value={draftFilters.name} onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Email" value={draftFilters.email} onChange={(e) => setDraftFilters((p) => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="support-customers-filter-status"
                      instanceId="support-customers-filter-status"
                      classNamePrefix="ns-support-customers-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="4" className="text-center">No customers found</td></tr>
                    ) : rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {(() => {
                            const c = nsAvatarColor(r.email || r.name);
                            return (
                              <div className="ns-requester">
                                <span className="ns-avatar" style={{ background: c.bg, color: c.text }}>
                                  {getAvatarInitials(r.name)}
                                </span>
                                <div style={{ minWidth: 0 }}>
                                  <div className="ns-requester-name">{r.name || "-"}</div>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td>{r.email || "-"}</td>
                        <td>
                          {(() => {
                            const st = getStatusDisplay(r.status);
                            return <span className={st.badgeClass}>{st.label}</span>;
                          })()}
                        </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === r.id}
                              onClick={(e) => handleOpenDropdown(e, r.id, r)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Support customers pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block ns-modal" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? "Edit Customer" : "Add Customer"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-2"><label className="form-label">Name</label><input className="form-control" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
                  <div className="mb-2"><label className="form-label">Email</label><input className="form-control" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} /></div>
                  <div className="mb-2">
                    <label className="form-label">Status</label>
                    <Select
                      inputId="support-customers-modal-status"
                      instanceId="support-customers-modal-status"
                      classNamePrefix="ns-support-customers-modal-status"
                      isSearchable={false}
                      isClearable={false}
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => o.value === Number(form.status)) || DEFAULT_STATUS_OPTIONS[0]}
                      onChange={(opt) => setForm((p) => ({ ...p, status: Number(opt?.value ?? 1) }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  {can(form.id ? "support.customers.edit" : "support.customers.add") ? (
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={save}><span>Save</span></button>
                  ) : null}
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => setShowModal(false)}><span>Close</span></button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "220px",
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() => handleDropdownAction(async () => {
                  const id = openActionItem.customer_id;
                  if (!id) return toast.error("Customer ID not available");
                  await copyToClipboard(id);
                  toast.success("Customer ID copied");
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdContentCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Copy Customer ID</span>
              </button>
              {can("support.customers.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => {
                  setForm({
                    id: openActionItem.id,
                    customer_id: openActionItem.customer_id,
                    name: openActionItem.name,
                    email: openActionItem.email || "",
                    status: Number(openActionItem.status) === 0 ? 0 : 1,
                  });
                  setShowModal(true);
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Edit Customer</span>
              </button>
              ) : null}
              {can("support.customers.change_status") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => toggleCustomerStatus(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                {Number(openActionItem.status) === 1 ? (
                  <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                ) : (
                  <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                )}
                <span style={{ fontSize: "0.875rem" }}>{Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}</span>
              </button>
              ) : null}
              {can("support.customers.delete") ? (
              <button
                type="button"
                className="delete-class"
                onClick={() => handleDropdownAction(() => remove(openActionItem.id))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Delete Customer</span>
              </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default AppSupportCustomers;
